import React, { useState, useEffect } from "react";
import { UserPlus, Trash2, Copy, Check, Shield, User, Mail, Clock, Lock, KeyRound, X, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Switch } from "./ui/switch";
import { api } from "../api";

const inputCls =
  "w-full bg-[#12131f] border border-white/10 rounded-lg pl-10 pr-4 py-2.5 text-white text-sm outline-none focus:border-[#25D366] transition-colors";

// Capability definitions shown as toggles
export const CAPS = [
  { key: "editUrls", label: "Edit Site URLs", hint: "WhatsApp, Telegram & download links" },
  { key: "editLogo", label: "Change Logo & Colour", hint: "Site logo image and accent colour" },
  { key: "editContent", label: "Edit Site Content", hint: "Name, tagline, heading & features" },
  { key: "createSite", label: "Create New Sites", hint: "Add brand-new sites" },
  { key: "deleteSite", label: "Delete Sites", hint: "Remove existing sites" },
  { key: "setActive", label: "Set Active Site", hint: "Choose which site the landing shows" },
  { key: "manageBrands", label: "Manage Featured Brands", hint: "Add, edit, reorder brand logos" },
];

const ALL_TRUE = CAPS.reduce((a, c) => ({ ...a, [c.key]: true }), {});
const ALL_FALSE = CAPS.reduce((a, c) => ({ ...a, [c.key]: false }), {});

const PermToggles = ({ value, onChange }) => (
  <div className="space-y-2">
    <div className="flex items-center gap-2 mb-2">
      <button type="button" onClick={() => onChange({ ...ALL_TRUE })} className="text-xs font-semibold text-[#25D366] hover:underline">Select all</button>
      <span className="text-white/20">·</span>
      <button type="button" onClick={() => onChange({ ...ALL_FALSE })} className="text-xs font-semibold text-white/50 hover:underline">Clear all</button>
    </div>
    {CAPS.map((c) => (
      <div key={c.key} className="flex items-center justify-between gap-3 bg-[#12131f] border border-white/10 rounded-lg px-3 py-2.5">
        <div className="min-w-0">
          <p className="text-white text-sm font-medium">{c.label}</p>
          <p className="text-white/40 text-[11px]">{c.hint}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className={`text-[10px] font-bold uppercase ${value[c.key] ? "text-[#25D366]" : "text-white/40"}`}>
            {value[c.key] ? "On" : "Off"}
          </span>
          <Switch
            checked={!!value[c.key]}
            onCheckedChange={(v) => onChange({ ...value, [c.key]: v })}
            className="data-[state=checked]:bg-[#25D366] data-[state=unchecked]:bg-white/20"
          />
        </div>
      </div>
    ))}
  </div>
);

const CreateAgentDialog = ({ open, onClose, onCreated }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [perms, setPerms] = useState({ ...ALL_TRUE });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (open) { setEmail(""); setPassword(""); setPerms({ ...ALL_TRUE }); setErr(""); }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!email.includes("@")) return setErr("Valid email required");
    if (password.length < 6) return setErr("Password must be at least 6 characters");
    setLoading(true);
    try {
      await api.createAgent(email.trim(), password, perms);
      setLoading(false);
      onCreated();
      onClose();
      toast.success("Agent created");
    } catch (e2) {
      setLoading(false);
      setErr(e2?.response?.data?.detail || "Failed to create agent");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#1b1d2b] border-white/10 text-white max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">Create Agent</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div>
            <label className="text-xs font-medium text-white/60 mb-1.5 block">Agent Email (Login ID)</label>
            <div className="relative">
              <Mail className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2" />
              <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" className={inputCls} placeholder="agent@email.com" />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-white/60 mb-1.5 block">Password</label>
            <div className="relative">
              <Lock className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2" />
              <input value={password} onChange={(e) => setPassword(e.target.value)} type="text" className={inputCls} placeholder="At least 6 characters" />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-white/60 mb-2 block">Permissions</label>
            <PermToggles value={perms} onChange={setPerms} />
          </div>
          {err && <p className="text-red-400 text-sm">{err}</p>}
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-lg font-semibold text-sm bg-white/10 hover:bg-white/15 transition-colors">Cancel</button>
            <button type="submit" disabled={loading} className="flex-1 py-2.5 rounded-lg font-semibold text-sm bg-[#25D366] hover:bg-[#1fb757] disabled:opacity-60 transition-colors">
              {loading ? "Creating..." : "Create Agent"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const EditPermsDialog = ({ open, onClose, agent, onSaved }) => {
  const [perms, setPerms] = useState({ ...ALL_TRUE });
  const [loading, setLoading] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (open && agent) setPerms({ ...ALL_FALSE, ...(agent.permissions || {}) });
  }, [open, agent]);

  const save = async () => {
    setLoading(true);
    try {
      await api.updateAgent(agent.id, perms);
      setLoading(false);
      onSaved();
      onClose();
      toast.success("Permissions updated");
    } catch (e) {
      setLoading(false);
      toast.error(e?.response?.data?.detail || "Failed to update");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#1b1d2b] border-white/10 text-white max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">Edit Permissions</DialogTitle>
        </DialogHeader>
        <p className="text-white/50 text-sm -mt-1 mb-2 truncate">{agent?.email}</p>
        <PermToggles value={perms} onChange={setPerms} />
        <div className="flex gap-3 pt-4">
          <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-lg font-semibold text-sm bg-white/10 hover:bg-white/15 transition-colors">Cancel</button>
          <button type="button" onClick={save} disabled={loading} className="flex-1 py-2.5 rounded-lg font-semibold text-sm bg-[#25D366] hover:bg-[#1fb757] disabled:opacity-60 transition-colors">
            {loading ? "Saving..." : "Save Permissions"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const LinkBox = ({ label, link, onClose }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  return (
    <div className="bg-[#12131f] border border-[#25D366]/40 rounded-lg p-3 mb-4 relative">
      <button onClick={onClose} className="absolute top-2 right-2 p-1 rounded hover:bg-white/10"><X className="w-3.5 h-3.5 text-white/50" /></button>
      <p className="text-xs text-white/60 mb-2 pr-6">{label}</p>
      <div className="flex items-center gap-2">
        <input readOnly value={link} className="flex-1 bg-transparent text-[#25D366] text-xs outline-none truncate" />
        <button onClick={copy} className="shrink-0 p-1.5 rounded-md bg-white/10 hover:bg-white/15 transition-colors">
          {copied ? <Check className="w-4 h-4 text-[#25D366]" /> : <Copy className="w-4 h-4 text-white/70" />}
        </button>
      </div>
    </div>
  );
};

const capCount = (a) => CAPS.filter((c) => a.permissions?.[c.key]).length;

const AgentsPanel = () => {
  const [agents, setAgents] = useState([]);
  const [email, setEmail] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [editAgent, setEditAgent] = useState(null);
  const [linkInfo, setLinkInfo] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try { setAgents(await api.listAgents()); } catch (e) { console.error("Failed to load agents:", e); toast.error("Unable to load agents."); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const invite = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    setLinkInfo(null);
    try {
      const data = await api.inviteAgent(email.trim(), { ...ALL_TRUE });
      const link = `${window.location.origin}/admin/invite?token=${data.invite_token}`;
      setLinkInfo({ label: "Share this invite link (agent sets own password). You can fine-tune their permissions after they join:", link });
      setEmail("");
      await load();
      toast.success("Invite created");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to invite");
    } finally {
      setLoading(false);
    }
  };

  const remove = async (id) => {
    try { await api.deleteAgent(id); await load(); toast.success("Agent removed"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed to remove"); }
  };

  const resetPass = async (id) => {
    try {
      const data = await api.resetAgent(id);
      const link = `${window.location.origin}/admin/reset?token=${data.reset_token}`;
      setLinkInfo({ label: `Password reset link for ${data.email} (valid 24 hours):`, link });
      toast.success("Reset link generated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to generate reset link");
    }
  };

  const inviteLinkFor = (a) => (a.invite_token ? `${window.location.origin}/admin/invite?token=${a.invite_token}` : null);
  const copyText = (text) => { navigator.clipboard.writeText(text); toast.success("Copied"); };

  return (
    <div className="bg-[#1b1d2b] border border-white/10 rounded-xl p-5">
      <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-[#25D366]" />
          <h2 className="font-display font-bold text-white">Team & Agents</h2>
        </div>
        <button onClick={() => setCreateOpen(true)} className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1fb757] text-white font-semibold text-sm px-4 py-2.5 rounded-lg transition-colors">
          <UserPlus className="w-4 h-4" /> Create Agent
        </button>
      </div>

      <form onSubmit={invite} className="flex flex-col sm:flex-row gap-2 mb-4">
        <div className="relative flex-1">
          <Mail className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="Invite by email (agent sets own password)" className={inputCls} />
        </div>
        <button type="submit" disabled={loading} className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/15 disabled:opacity-60 text-white font-semibold text-sm px-4 py-2.5 rounded-lg transition-colors whitespace-nowrap">
          <UserPlus className="w-4 h-4" /> Invite Link
        </button>
      </form>

      {linkInfo && <LinkBox label={linkInfo.label} link={linkInfo.link} onClose={() => setLinkInfo(null)} />}

      <div className="space-y-2">
        {agents.map((a) => {
          const isSuper = a.role === "super_admin";
          const count = capCount(a);
          return (
            <div key={a.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-[#12131f] border border-white/10 rounded-lg px-4 py-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${isSuper ? "bg-[#25D366]/15" : "bg-white/10"}`}>
                  {isSuper ? <Shield className="w-4 h-4 text-[#25D366]" /> : <User className="w-4 h-4 text-white/70" />}
                </div>
                <div className="min-w-0">
                  <p className="text-white text-sm truncate">{a.email}</p>
                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                    <span className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full ${isSuper ? "text-[#25D366] bg-[#25D366]/15" : "text-white/70 bg-white/10"}`}>
                      {isSuper ? "Super Admin" : "Agent"}
                    </span>
                    {!isSuper && (
                      <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full text-[#5bb9e6] bg-[#229ED9]/15">
                        {count === CAPS.length ? "Full access" : count === 0 ? "View only" : `${count} permission${count > 1 ? "s" : ""}`}
                      </span>
                    )}
                    {a.status === "invited" && (
                      <span className="text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full text-amber-300 bg-amber-400/15 inline-flex items-center gap-1">
                        <Clock className="w-3 h-3" /> Pending
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {!isSuper && (
                <div className="flex items-center gap-2 shrink-0 flex-wrap">
                  <button onClick={() => setEditAgent(a)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/15 text-white/80 text-xs font-semibold transition-colors">
                    <SlidersHorizontal className="w-3.5 h-3.5" /> Permissions
                  </button>
                  {a.status === "active" && (
                    <button onClick={() => resetPass(a.id)} title="Reset password" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/15 text-white/80 text-xs font-semibold transition-colors">
                      <KeyRound className="w-3.5 h-3.5" /> Reset
                    </button>
                  )}
                  {a.status === "invited" && inviteLinkFor(a) && (
                    <button onClick={() => copyText(inviteLinkFor(a))} title="Copy invite link" className="p-2 rounded-lg hover:bg-white/10 transition-colors">
                      <Copy className="w-4 h-4 text-white/70" />
                    </button>
                  )}
                  <button onClick={() => remove(a.id)} title="Remove" className="p-2 rounded-lg hover:bg-red-500/15 transition-colors">
                    <Trash2 className="w-4 h-4 text-red-400" />
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <CreateAgentDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreated={load} />
      <EditPermsDialog open={!!editAgent} agent={editAgent} onClose={() => setEditAgent(null)} onSaved={load} />
    </div>
  );
};

export default AgentsPanel;
